import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { SelectFilter, applyFilter, makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, SimpleBarChart } from '@/components/ReportCharts'

interface Row {
  id: number
  agent_name: string
  agent_email: string | null
  policies: number
  gross_amount: number
  agent_amount: number
  override_earned: number
}

interface Props extends PageProps {
  rows: Row[]
  date_range: DateRange
  carriers: { id: number; name: string }[]
  filters: { carrier_id: number | null; commission_type: string | null; status: string | null }
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/top_producers'

function money(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export default function TopProducers({ rows, date_range, carriers, filters, sort }: Props) {
  const filterState = {
    carrier_id: filters.carrier_id ?? '',
    commission_type: filters.commission_type ?? '',
    status: filters.status ?? ''
  }
  const onSortChange = makeSortHandler(PATH, date_range, filterState)

  function set(key: string, value: string) {
    applyFilter(PATH, { ...filterState, preset: date_range.preset, start_date: date_range.start_date, end_date: date_range.end_date }, key, value)
  }

  const columns: Column<Row>[] = [
    { key: 'agent_name', header: 'Agent', sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{r.agent_name}</div>
          {r.agent_email && <div className="text-xs text-chestnut-charcoal/60">{r.agent_email}</div>}
        </div>
      ) },
    { key: 'policies', header: 'Policies', sortable: true, className: 'text-center' },
    { key: 'gross_amount', header: 'Gross $', sortable: true, render: (r) => money(r.gross_amount) },
    { key: 'agent_amount', header: 'Agent $', sortable: true, render: (r) => money(r.agent_amount) },
    { key: 'override_earned', header: 'Override Earned', sortable: true, render: (r) => money(r.override_earned) }
  ]

  return (
    <AppLayout>
      <Head title="Top Producers" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Top Producers</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Agents ranked by production. Use the column headers to re-sort.</p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} filters={filterState} sortState={sort}>
          <SelectFilter
            label="Carrier"
            value={filters.carrier_id ?? ''}
            options={carriers.map(c => ({ value: c.id, label: c.name }))}
            onChange={(v) => set('carrier_id', v)}
          />
          <SelectFilter
            label="Type"
            value={filters.commission_type ?? ''}
            options={[
              { value: 'first_year', label: 'First Year' },
              { value: 'renewal', label: 'Renewal' }
            ]}
            onChange={(v) => set('commission_type', v)}
          />
          <SelectFilter
            label="Status"
            value={filters.status ?? ''}
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'paid', label: 'Paid' }
            ]}
            onChange={(v) => set('status', v)}
          />
        </ReportControlBar>

        <ChartCard
          title="Top 10 producers by gross"
          subtitle="Reflects current filters and date range. Sorted by gross, descending."
          empty={rows.length === 0}
          emptyMessage="No commissions match this filter."
        >
          <SimpleBarChart
            data={[...rows]
              .sort((a, b) => b.gross_amount - a.gross_amount)
              .slice(0, 10)
              .map(r => ({ label: r.agent_name, value: r.gross_amount }))}
            tooltipLabel="Gross"
          />
        </ChartCard>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search agents..."
            searchKeys={['agent_name', 'agent_email']}
            pageSize={20}
            emptyMessage="No commissions match this filter."
            getRowKey={(r) => r.id}
            controlledSort={{ key: sort.key ?? 'gross_amount', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>
      </div>
    </AppLayout>
  )
}
